/**
 * The Preview of a native device app (utils/deviceApps.js: `swift-ios`, `kotlin-android`): it has no web page, so
 * instead of the runner's "This is a native app…" error, what it is ("This is an iPhone app — it runs on a device") and
 * the ways to build it: its builds in Apps, Build for devices (DeviceBuildDialog: Codemagic, a Mac, Flow's cluster),
 * `flow build` on a Mac (Flow CLI), and Codemagic (connected in Features › Connections). Nothing starts from here.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { DeviceMobile } from '@phosphor-icons/react';
import { DeviceBuildButton } from '../devicebuilds/DeviceBuildEntry';

const shortName = (serviceName) => String(serviceName || '').replace(/^services\//, '');

/** "an iPhone app" / "an Android app" / "a mobile app", from the platforms the stack builds for. */
export function deviceAppKind(app) {
  const platforms = app?.platforms || [];
  if (platforms.length === 1 && platforms[0] === 'ios') { return 'an iPhone app'; }
  if (platforms.length === 1 && platforms[0] === 'android') { return 'an Android app'; }
  return 'a mobile app';
}

export default function DeviceAppPreview({
  app, projectId, serviceName, readOnly = false, onOpenBuilds = null, onOpenConnections = null,
}) {
  const Icon = app?.Icon || DeviceMobile;
  const platforms = app?.platforms || [];
  const platform = platforms.length === 1 ? platforms[0] : null;
  const name = shortName(serviceName);
  const macCommand = `flow build ${name}${platform ? ` --platform ${platform}` : ''}`;
  return (
    <div className="preview-device-app" data-testid="preview-device-app" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '12px', textAlign: 'center', padding: '24px' }}>
      <Icon aria-hidden size={32} />
      <h3 style={{ margin: 0 }}>{`This is ${deviceAppKind(app)} — it runs on a device`}</h3>
      <p style={{ margin: 0, maxWidth: '420px', opacity: 0.8 }}>
        {`${app?.label || name} has no web preview. Build it to install it on a phone, or to send it to the stores.`}
      </p>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {onOpenBuilds && <Button data-testid="device-open-builds" onClick={onOpenBuilds} type="primary">Open builds</Button>}
        {projectId && <DeviceBuildButton projectId={projectId} readOnly={readOnly} service={serviceName} />}
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', maxWidth: '460px', opacity: 0.85, display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <li data-testid="device-mac-hint">On a Mac with the Flow CLI: <code>{macCommand}</code></li>
        <li data-testid="device-codemagic-hint">
          {'In the cloud with '}
          <a href="https://codemagic.io/apps" rel="noopener noreferrer" target="_blank">Codemagic</a>
          {onOpenConnections ? (
            <>
              {': connect it in '}
              <Button data-testid="device-open-connections" onClick={onOpenConnections} size="small" style={{ padding: 0 }} type="link">Connections</Button>
            </>
          ) : null}
        </li>
      </ul>
    </div>
  );
}

DeviceAppPreview.propTypes = {
  app: PropTypes.shape({ label: PropTypes.string, platforms: PropTypes.arrayOf(PropTypes.string), Icon: PropTypes.elementType }),
  onOpenBuilds: PropTypes.func,
  onOpenConnections: PropTypes.func,
  projectId: PropTypes.string,
  readOnly: PropTypes.bool,
  serviceName: PropTypes.string.isRequired,
};
